/* eslint-disable no-param-reassign */
import _ from 'lodash';

/*
 * Capitalize(text)
 *
 * Converts the first character of a text to capital (uppercase) letter and remaining characters to lowercase.
 *
 * SAMPLES
 * Capitalize("hello world") ->
 * Capitalize("Hi All") Hi all
 *
 * INPUT
 * text: The string to be converted.
 *
 * OUTPUT
 * Returns the text with the first character made uppercase.
 */
function Capitalize(text) {
  return text === null || text === undefined || text.length === 0
    ? ''
    : text.charAt(0).toUpperCase() + text.slice(1);
}

/*
 * Concat(value, …)
 *
 * This function returns a concatenated string of several items that had been used as parameters
 *
 * SAMPLES
 * Concat("$",@amount) -> $10
 * Concat("New"," ","York") -> New York
 *
 * INPUT
 * values: The strings to concat.
 *
 * OUTPUT
 * Returns a concatenated string with the values used as parameters
 */
function Concat(...values) {
  if (values === null || values === undefined || values.length === 0) {
    return '';
  }
  return values.join('');
}

/*
 * Contains(value, search)
 *
 * Checks if a string contains the search value
 *
 * SAMPLES
 * Contains("Hello there!", "there") -> True
 * Contains("Hello there!", "there?") -> False
 *
 * INPUT
 * value: The string to be checked.
 * search: The term to be searched in
 *
 * OUTPUT
 * Returns True if the search term is contained in value, otherwise False
 */
function Contains(value, search) {
  return value === null ||
    value === undefined ||
    value.length === 0 ||
    search === null ||
    search === undefined ||
    search.length === 0
    ? false
    : value.includes(search);
}

/*
 * Length(value)
 *
 * Gets the length of the given string
 *
 * SAMPLES
 * Length("Hi there!") -> 9
 *
 * INPUT
 * value: The string to be checked
 *
 * OUTPUT
 * returns the length of characters
 */
function Length(value) {
  return value == null ? 0 : String(value).length;
}

/*
 * Lower(text)
 *
 * Converts the text to all lowercase letters
 *
 * SAMPLES
 * Lower("Hi ALL") -> hi all
 *
 * INPUT
 * text: The string to be converted to lowercase
 *
 * OUTPUT
 * Returns the text with all characters made lowercase
 */
function Lower(text) {
  return text === null || text === undefined || text.length === 0 ? '' : _.toLower(text);
}

/*
 * Upper(text)
 *
 * Converts the text to all uppercase letters.
 *
 * SAMPLES
 * Upper("Hi all!") -> HI ALL!
 *
 * INPUT
 * text: The string to be converted to uppercase.
 *
 * OUTPUT
 * Returns the text with all characters made uppercase.
 */
function Upper(text) {
  return text === null || text === undefined || text.length === 0 ? '' : _.toUpper(text);
}

/*
 * Replace(text, old, new)
 *
 * Replaces all occurrences of a substring within a text, with a new substring
 *
 * SAMPLES
 * Replace("Hello!", "llo", "y") -> "Hey!"
 * Replace("hahaha", "a", "e") -> "hehehe"
 *
 * INPUT
 * text: The original string
 * old: The string to be replaced
 * new: The new replacement string
 *
 * OUTPUT
 * Returns a new string with all matches of old substring replaced by the new substring
 */
function Replace(text, old, replaceValue) {
  if (text === null || text === undefined) {
    return '';
  }
  if (old === null || old === undefined || old.length === 0) {
    return text;
  }

  return text.replace(old, replaceValue);
}

/*
 * Slice(value, start, end)
 *
 * Extracts some consecutive elements from an string
 *
 * SAMPLES
 * Slice("Hello world", 4, 8) -> "o wo"
 * Slice("Hello world", 4, -1) -> "o worl"
 * Slice("Hello world", 4) -> "o world"
 *
 * INPUT
 * value: The string to be processed
 * start: Optional.The position to start from.Counts from 0.
 * end: Optional.The position to end at.Counts from 0.
 *
 * OUTPUT
 * Returns a part of the provided value based on the start and end positions
 */
function Slice(value, start, end) {
  if (value == null || value.length === 0) return '';
  return Number.isNaN(value) ? value.slice(start, end) : value.toString().slice(start, end);
}

/*
 * Title(text)
 *
 * Converts the first character in each word to capital (uppercase) letter and remaining characters to lowercase
 *
 * Title("hello world") -> Hello World
 * Title("MY friend") -> My Friend
 *
 * INPUT
 * text: The string to be converted to title
 *
 * OUTPUT
 * Returns the text with first characters of all the words made capitalized
 */
function Title(text) {
  if (text === null || text === undefined || text.length === 0) {
    return '';
  }
  const sentence = text.toLowerCase().split(' ');
  for (let i = 0; i < sentence.length; i++) {
    sentence[i] = sentence[i][0].toUpperCase() + sentence[i].slice(1);
  }
  return sentence.join(' ');
}

/*
 * PadStart(text, targetLength, padString)
 *
 * The method pads the current string with another string (multiple times, if needed) until the resulting string reaches the given length
 *
 * PadStart("5", 4, "0") -> "0005"
 * PadStart("0980", 5, "0") -> "00980"
 * PadStart("0012345", 8, "CE") -> "CEC0012345"
 *
 * INPUT
 * text: The string to apply PadStart
 * targetLength: The length of the resulting string once the current str has been padded. If the value is less than str.length, then str is returned as-is.
 * padString: The string to pad the current str with. If padString is too long to stay within the targetLength, it will be truncated from the end
 *
 * OUTPUT
 * A String of the specified targetLength with padString applied from the start
 */
function PadStart(text, targetLength, padString) {
  if (text == null) text = '';
  return targetLength === 0 ? text : text.toString().padStart(targetLength, padString);
}

/*
 * PadEnd(text, targetLength, padString)
 *
 * The method pads the current string with another string (multiple times, if needed) until the resulting string reaches the given length
 *
 * PadEnd("5", 4, "0") -> "5000"
 * PadEnd("0980", 5, "0") -> "09800"
 * PadEnd("0012345", 8, "CE") -> "0012345CEC"
 *
 * INPUT
 * text: The string to apply PadEnd
 * targetLength: The length of the resulting string once the current str has been padded. If the value is less than str.length, then str is returned as-is.
 * padString: The string to pad the current str with. If padString is too long to stay within the targetLength, it will be truncated from the end
 *
 * OUTPUT
 * A String of the specified targetLength with padString applied from the end
 */
function PadEnd(text, targetLength, padString) {
  if (text == null) text = '';
  return targetLength === 0 ? text : text.toString().padEnd(targetLength, padString);
}

/*
 * ToString(value)
 *
 * Converts a value to text.
 *
 * ToString(10.45) -> "10.45"
 * ToString(Concat(1233174,'-',323147.16)) -> "1233174-323147.16"
 *
 * INPUT
 * value: The value to be converted to string.
 *
 * OUTPUT
 * Returns the value as text.
 */
function ToString(value) {
  return String(value);
}

export default {
  Capitalize,
  Concat,
  Contains,
  Length,
  Lower,
  Upper,
  Replace,
  Slice,
  Title,
  PadStart,
  PadEnd,
  ToString,
};
